import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Map,
  FileText,
  Users,
  Calendar,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  ArrowRight,
  Briefcase,
  Shield,
  Zap,
  ChevronRight,
} from "lucide-react";

const ServicesPage = () => {
  const navigate = useNavigate();

  const landPoolingSteps = [
    {
      icon: CheckSquare,
      title: "Eligibility Check",
      description: "Verify whether your Delhi parcel falls within a notified DDA zone (J, K-I, L, N, P-I, P-II).",
    },
    {
      icon: FileText,
      title: "Document Upload",
      description: "Upload ownership deed, khasra/khatauni records, mutation certificate and identity proof.",
    },
    {
      icon: Zap,
      title: "Verification",
      description: "Documents are checked against DDA requirements and 70% contiguity is confirmed.",
    },
    {
      icon: Shield,
      title: "Share Estimation",
      description: "See your estimated share of returned developed land based on the 60:40 rule.",
    },
  ];

  const consortiumModules = [
    {
      id: "agreements",
      icon: FileText,
      title: "Agreement Formats",
      description:
        "Library of illustrative consortium agreement formats including Basic, Revenue Share, Area Share, and Hybrid models.",
      features: [
        "Download/Preview agreements",
        "Book a Lawyer option",
        "Version control & audit trail",
        "Custom clause editing",
      ],
    },
    {
      id: "administration",
      icon: Users,
      title: "Administration & Functionality",
      description:
        "Digitally run the consortium like a professional organization with complete management tools.",
      features: [
        "Member directory",
        "Calendar & schedules",
        "Meeting coordination",
        "Reports & compliance logs",
      ],
    },
    {
      id: "voting",
      icon: Vote,
      title: "Voting Process Management",
      description:
        "Enable transparent assent/dissent decision-making with weighted voting based on land contribution.",
      features: [
        "Agenda-based voting",
        "Area/share based weightage",
        "Time-bound voting windows",
        "Immutable voting records",
      ],
    },
    {
      id: "rfp",
      icon: FileSpreadsheet,
      title: "RFPs & Bid Management",
      description:
        "Help landowners professionally engage developers through a structured RFP and bidding process.",
      features: [
        "RFP & bid templates",
        "Custom evaluation matrix",
        "Developer invitation portal",
        "Bid comparison dashboard",
      ],
    },
    {
      id: "consent",
      icon: CheckSquare,
      title: "Consent Management",
      description:
        "Make joining and participating legally valid with secure OTP/e-sign verification.",
      features: [
        "Digital consent forms",
        "OTP/e-sign verification",
        "Consent withdrawal option",
        "Compliance-ready records",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#4E80FF] to-[#2952CC] py-20">
        <div className="container-main">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white/90 mb-6">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Delhi &middot; DDA Land Pooling Policy 2018
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl text-white/90">
              End-to-end digital tools built specifically for DDA&apos;s Land
              Pooling Policy 2018 &mdash; from eligibility checks and consortium
              formation to voting, RFPs and consent management.
            </p>
          </div>
        </div>
      </section>

      {/* Land Pooling Process */}
      <section id="land-pooling" className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#4E80FF] px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Map className="w-4 h-4" />
              Service 1
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Land Pooling Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our core engine that handles eligibility, verification, and
              valuation for DDA Land Pooling zones (J, K-I, L, N, P-I, P-II).
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {landPoolingSteps.map((step, index) => (
              <Card key={index} className="relative card-hover">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-[#4E80FF]" />
                  </div>
                  <span className="absolute top-4 right-4 text-4xl font-bold text-slate-100">
                    {index + 1}
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              className="btn-primary"
              onClick={() => navigate("/register")}
              data-testid="start-pooling-btn"
            >
              Start Land Pooling
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Consortium Management */}
      <section id="consortium" className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#4E80FF] px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Briefcase className="w-4 h-4" />
              Service 2
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Consortium Management Process™
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive module for managing all aspects of landowner
              consortiums with 5 powerful sub-modules.
            </p>
          </div>

          <Tabs defaultValue="agreements" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto mb-8">
              {consortiumModules.map((module) => (
                <TabsTrigger
                  key={module.id}
                  value={module.id}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg data-[state=active]:bg-[#4E80FF] data-[state=active]:text-white border border-slate-200 data-[state=active]:border-[#4E80FF]"
                >
                  <module.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{module.title}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {consortiumModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div>
                        <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center mb-6">
                          <module.icon className="w-8 h-8 text-[#4E80FF]" />
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                          {module.title}
                        </h3>
                        <p className="text-lg text-slate-600 mb-6">
                          {module.description}
                        </p>
                        <ul className="space-y-3">
                          {module.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <ChevronRight className="w-5 h-5 text-[#4E80FF]" />
                              <span className="text-slate-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-slate-100 rounded-xl p-8 h-64 flex items-center justify-center">
                        <div className="text-center">
                          <module.icon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-400">
                            Interactive {module.title} Interface
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              End-to-End Process Flow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From registration to development, we guide you through every step.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Flow Diagram */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {[
                { label: "Login", color: "bg-[#4E80FF]" },
                { label: "Verify Land", color: "bg-[#4E80FF]" },
                { label: "Join Consortium", color: "bg-[#4E80FF]" },
                { label: "Sign Agreement", color: "bg-[#4E80FF]" },
                { label: "Vote & Decide", color: "bg-[#4E80FF]" },
                { label: "Develop", color: "bg-[#4E80FF]" },
              ].map((step, index, arr) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-20 h-20 rounded-full ${step.color} flex items-center justify-center text-white font-bold shadow-lg`}
                    >
                      {index + 1}
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-700">
                      {step.label}
                    </p>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className="w-6 h-6 text-slate-300 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#4E80FF] to-[#2952CC]">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Experience the Future of Land Management
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Join our platform and access all these services with a single account.
            Start your journey today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-[#4E80FF] hover:bg-white/90"
              onClick={() => navigate("/register")}
              data-testid="services-register-btn"
            >
              Create Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
